// Moving a v3 sizing in and out of the app: the `?s=` share link and the saved-settings markdown
// file. Pure and math-free (no engine, no v2 modules), so v2's engine/share.ts can be deleted.
//
// Both carry the same payload, `V3PayloadSchema` (`v: 3`). Reading is NOT done here: the raw JSON
// goes through migrate-v3's `migrateSharePayload` / `migrateConfigImport`, which also accept every
// v2 payload ever written. Encoding is a base64 of UTF-8 JSON, the same codec as v2 so a link is
// still a plain `?s=<base64>`.

import { safeParseJson } from '../../_shared/parse-json';
import { z } from 'zod';
import { getSizedUseCase } from '../data/use-case-catalog';
import { TOOLS16 } from '../data/tools';
import { money } from './format';
import { V3_PAYLOAD_VERSION, type Recheck, type SizingState, type V3Payload } from './migrate-v3';

export function v3Payload(sizing: SizingState, recheck: Recheck[] = []): V3Payload {
  return { v: V3_PAYLOAD_VERSION, ...sizing, recheck };
}

/** UTF-8 JSON -> base64. Null when encoding fails. */
export function encodeShareV3(payload: V3Payload): string | null {
  try {
    const bytes = new TextEncoder().encode(JSON.stringify(payload));
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  } catch {
    return null;
  }
}

/**
 * base64 -> parsed JSON, unvalidated (migrateSharePayload validates it). Null on bad base64, bad
 * UTF-8 or bad JSON; never throws.
 */
export function decodeShareRaw(b64: string): unknown {
  try {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const json = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    const parsed = safeParseJson(z.unknown(), json);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ settings markdown */

export function settingsFilename(customerName: string, dateStr: string): string {
  const cn = (customerName || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return ['clay-calculator', cn, dateStr].filter(Boolean).join('-') + '.md';
}

/** `YYYY-MM-DD` in local time, as v2's filename used. */
export function settingsDateStr(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

const answered = (v: number | '' | null | undefined): v is number => typeof v === 'number';

/**
 * The saved-settings file: a readable summary, then the machine-readable
 * `<!-- clay:settings {json} -->` trailer that migrateConfigImport reads back.
 */
export function buildSettingsMarkdown(payload: V3Payload): string {
  const L: string[] = ['# Value calculator settings', ''];
  if (payload.customerName) L.push(`Customer: ${payload.customerName}`, '');
  const d = payload.deal;
  L.push(
    '## Deal economics',
    '',
    `- Average deal size (ACV): ${money(d.acv)}`,
    `- Win rate: ${d.winRate}%`,
    `- Clay contract estimate: ${d.contractEstimate == null ? 'not set' : money(d.contractEstimate)}`,
  );
  if (d.currentAnnualPipeline != null) L.push(`- Current annual pipeline: ${money(d.currentAnnualPipeline)}`);
  L.push('');
  for (const sel of payload.selected) {
    const uc = getSizedUseCase(sel.id);
    if (!uc) continue;
    L.push(`## ${uc.name}`, '');
    if (sel.recordLevels?.length) L.push(`- Record levels: ${sel.recordLevels.join(', ')}`);
    if (sel.methods?.length) L.push(`- Calculation methods: ${sel.methods.join(', ')}`);
    for (const q of uc.questions) {
      const v = sel.answers?.[q.key];
      if (answered(v)) L.push(`- ${q.label}: ${v}`);
    }
    if (answered(sel.realization)) L.push(`- Realization: ${sel.realization}%`);
    if (answered(sel.attribution)) L.push(`- Attribution: ${sel.attribution}%`);
    if (uc.id === 'uc-tool-consolidation') {
      for (const t of TOOLS16) {
        const v = payload.tools?.[t.id];
        if (v != null) L.push(`- ${t.name}: ${v === '' ? 'blank' : money(v)} per year`);
      }
      for (const t of payload.customTools) L.push(`- ${t.name || 'Custom tool'}: ${answered(t.spend) ? money(t.spend) : 'blank'} per year`);
    }
    L.push('');
  }
  L.push('<!-- clay:settings', JSON.stringify(payload), '-->', '');
  return L.join('\n');
}
